import { useState, useEffect } from 'react';
import { companyAPI } from '../../../services/api';

export interface ProductionItem {
  recipe_id: number | '';
  quantity: number;
}

export interface StaffShift {
  name: string;
  start_time: string;
  end_time: string;
}

export interface StarterItem {
  starter_id: number | '';
  current_weight_g: number;
}

export interface Equipment {
  mixer_max_kg: number;
  oven_capacity: number;
  proofer_capacity: number;
}

export interface OptimizationResult {
  summary: {
    total_recipes: number;
    total_dough_kg: number;
    mixer_loads: number;
    oven_loads: number;
    estimated_pieces: number;
    pieces_label: string;
    total_labor_hours: number;
  };
  mixer_batches: {
    batch_number: number;
    total_kg: number;
    utilization_pct: number;
    recipes: { recipe_name: string; batch_kg: number; fraction: number }[];
  }[];
  ingredient_totals: { name: string; ingredient_id: number | null; quantity: number; unit: string; is_starter: boolean }[];
  starter_needed_g: number;
  starter_feeds: {
    timing: string;
    time_suggestion: string;
    action: string;
    from_g: number;
    to_g: number;
    add_flour_g: number;
    add_water_g: number;
    note: string;
  }[];
  oven_schedule: { batch: number; pieces: number; duration_min: number }[];
  proofer_schedule: { batch: number; pieces: number }[];
  labor: { mixing_min: number; shaping_min: number; baking_min: number; cleaning_min: number; total_min: number; total_hours: number };
  timeline: { time: string; step: string; duration_min: number; note: string; category: string }[];
  waste_warnings: { type: string; batch: number; utilization_pct: number; message: string; suggestion: string }[];
}

export interface Production {
  id: number;
  name: string;
  status: 'waiting' | 'started' | 'completed' | 'cancelled';
  status_display: string;
  production_date: string;
  notes: string;
  inventory_deducted: boolean;
  responsible_user: number | null;
  responsible_user_name: string | null;
  ingredients: { id: number; ingredient: number | null; ingredient_name: string; quantity: string; unit: string; is_starter: boolean }[];
  created_at: string;
}

const defaultEquipment: Equipment = {
  mixer_max_kg: 20,
  oven_capacity: 8,
  proofer_capacity: 20,
};

export function useCompanyBatchOptimization() {
  const [recipes, setRecipes] = useState<any[]>([]);
  const [starters, setStarters] = useState<any[]>([]);
  const [productionItems, setProductionItems] = useState<ProductionItem[]>([{ recipe_id: '', quantity: 1 }]);
  const [equipment, setEquipment] = useState<Equipment>(defaultEquipment);
  const [staff, setStaff] = useState<StaffShift[]>([]);
  const [starterInventory, setStarterInventory] = useState<StarterItem[]>([]);
  const [result, setResult] = useState<OptimizationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'batches' | 'ingredients' | 'starter'>('overview');

  const [productions, setProductions] = useState<Production[]>([]);
  const [productionLoading, setProductionLoading] = useState(false);
  const [productionError, setProductionError] = useState('');
  const [lowStockAlerts, setLowStockAlerts] = useState<{ ingredient_id: number; name: string; unit: string; current_stock: string; critical_amount: string }[]>([]);
  const [staffMembers, setStaffMembers] = useState<{ id: number; first_name: string; last_name: string; username: string }[]>([]);

  useEffect(() => {
    companyAPI.getRecipes().then(r => setRecipes(r.data)).catch(() => {});
    companyAPI.getStarters().then(r => setStarters(r.data)).catch(() => {});
    companyAPI.getProductions().then(r => setProductions(r.data)).catch(() => {});
    companyAPI.getStaffList().then(r => setStaffMembers(r.data)).catch(() => {});
  }, []);

  const addProductionItem = () => setProductionItems(prev => [...prev, { recipe_id: '', quantity: 1 }]);
  const removeProductionItem = (idx: number) => setProductionItems(prev => prev.filter((_, i) => i !== idx));
  const updateProductionItem = (idx: number, field: keyof ProductionItem, value: any) => {
    setProductionItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const updateEquipment = (field: keyof Equipment, value: number) => {
    setEquipment(prev => ({ ...prev, [field]: value }));
  };

  const addStaff = () => setStaff(prev => [...prev, { name: '', start_time: '08:00', end_time: '16:00' }]);
  const removeStaff = (idx: number) => setStaff(prev => prev.filter((_, i) => i !== idx));
  const updateStaff = (idx: number, field: keyof StaffShift, value: string) => {
    setStaff(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addStarterItem = () => setStarterInventory(prev => [...prev, { starter_id: '', current_weight_g: 100 }]);
  const removeStarterItem = (idx: number) => setStarterInventory(prev => prev.filter((_, i) => i !== idx));
  const updateStarterItem = (idx: number, field: keyof StarterItem, value: any) => {
    setStarterInventory(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const runOptimization = async () => {
    const validItems = productionItems.filter(i => i.recipe_id && i.quantity > 0);
    if (validItems.length === 0) {
      setError('Add at least one production item with a recipe and quantity.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        production_items: validItems.map(i => ({ recipe_id: i.recipe_id, quantity: i.quantity })),
        equipment,
        staff: staff.filter(s => s.name || s.start_time),
        starter_inventory: starterInventory.filter(s => s.starter_id && s.current_weight_g > 0).map(s => ({
          starter_id: s.starter_id,
          current_weight_g: s.current_weight_g,
        })),
      };
      const res = await companyAPI.batchOptimize(payload);
      setResult(res.data);
      setActiveTab('overview');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Optimization failed. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  };

  const startProduction = async (productionDate: string, productionName: string, responsibleUserId?: number | null) => {
    if (!result) return;
    setProductionLoading(true);
    setProductionError('');
    setLowStockAlerts([]);
    try {
      const res = await companyAPI.startProduction({
        name: productionName,
        production_date: productionDate,
        ingredient_totals: result.ingredient_totals,
        responsible_user_id: responsibleUserId || null,
      });
      setProductions(prev => [res.data.production, ...prev]);
      if (res.data.low_stock_alerts?.length > 0) {
        setLowStockAlerts(res.data.low_stock_alerts);
      }
      return res.data;
    } catch (e: any) {
      setProductionError(e.response?.data?.error || 'Failed to start production.');
    } finally {
      setProductionLoading(false);
    }
  };

  const updateProductionStatus = async (id: number, newStatus: string) => {
    try {
      const res = await companyAPI.updateProductionStatus(id, { status: newStatus });
      setProductions(prev => prev.map(p => p.id === id ? res.data : p));
    } catch (e: any) {
      setProductionError(e.response?.data?.error || 'Failed to update status.');
    }
  };

  return {
    recipes, starters,
    productionItems, addProductionItem, removeProductionItem, updateProductionItem,
    equipment, updateEquipment,
    staff, addStaff, removeStaff, updateStaff,
    starterInventory, addStarterItem, removeStarterItem, updateStarterItem,
    result, loading, error, setError,
    activeTab, setActiveTab,
    runOptimization,
    productions, productionLoading, productionError, setProductionError, lowStockAlerts, setLowStockAlerts,
    startProduction, updateProductionStatus,
    staffMembers,
  };
}
